import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitch from "@/components/LanguageSwitch";
import { Button } from "@/components/ui/button";
import { BrainCircuit, Target, GitBranch, Grid3x3, TrendingDown, Rocket, ArrowRight, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const login = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const HERO_BG = "https://images.unsplash.com/photo-1638864616275-9f0b291a2eb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBmaW5hbmNlJTIwZ2VvbWV0cmljfGVufDB8fHx8MTc4MjU0MDM2Nnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const features = [
    { icon: Target, t: t("landing.f1_t"), d: t("landing.f1_d"), c: "#8B5CF6" },
    { icon: GitBranch, t: t("landing.f2_t"), d: t("landing.f2_d"), c: "#3B82F6" },
    { icon: Grid3x3, t: t("landing.f3_t"), d: t("landing.f3_d"), c: "#EAB308" },
    { icon: TrendingDown, t: t("landing.f4_t"), d: t("landing.f4_d"), c: "#F59E0B" },
    { icon: Rocket, t: t("landing.f5_t"), d: t("landing.f5_d"), c: "#10B981" },
  ];
  const steps = [
    { t: t("landing.s1_t"), d: t("landing.s1_d") },
    { t: t("landing.s2_t"), d: t("landing.s2_d") },
    { t: t("landing.s3_t"), d: t("landing.s3_d") },
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14] text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-primary/15 border border-primary/40 flex items-center justify-center shadow-[0_0_15px_rgba(124,58,237,0.25)]">
              <BrainCircuit className="h-5 w-5 text-primary" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold">{t("brand")}</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">{t("tagline")}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors">{t("nav.features")}</a>
            <a href="#how" className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors">{t("nav.how")}</a>
            <LanguageSwitch />
            <Button data-testid="nav-login-btn" onClick={user ? () => (window.location.href = "/dashboard") : login}
              className="bg-primary text-white hover:bg-[#6D28D9] shadow-[0_0_15px_rgba(124,58,237,0.3)] hover:shadow-[0_0_25px_rgba(124,58,237,0.5)] rounded-lg">
              {user ? t("nav.dashboard") : t("nav.login")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/60 via-[#0B0E14]/85 to-[#0B0E14]" />
        <div className="absolute inset-0 grain-overlay opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-mono-fin text-[#C4B5FD] mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> {t("landing.badge")}
            </div>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              {t("landing.title_1")}<br />
              <span className="bg-gradient-to-r from-[#A78BFA] via-[#F59E0B] to-[#EAB308] bg-clip-text text-transparent">{t("landing.title_2")}</span>
            </h1>
            <p className="mt-7 text-lg text-muted-foreground max-w-2xl leading-relaxed">{t("landing.subtitle")}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button data-testid="hero-cta-btn" onClick={user ? () => (window.location.href = "/dashboard") : login}
                className="bg-primary text-white hover:bg-[#6D28D9] h-12 px-7 rounded-lg text-base shadow-[0_0_20px_rgba(124,58,237,0.35)] hover:shadow-[0_0_30px_rgba(124,58,237,0.55)] group">
                {t("landing.cta_primary")} <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                className="h-12 px-7 rounded-lg text-base bg-transparent border-border text-foreground hover:bg-[#1E2532] hover:text-white">
                {t("landing.cta_secondary")}
              </Button>
            </div>
            <p className="mt-8 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-[#10B981]" /> {t("landing.social")}</p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="max-w-2xl mb-14">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">{t("landing.features_title")}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{t("landing.features_sub")}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="rounded-xl bg-[#151A22] border border-border p-7 hover:-translate-y-1 hover:border-[#3a4456] transition-all duration-200">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center mb-5" style={{ background: `${f.c}1f`, border: `1px solid ${f.c}55` }}>
                  <Icon className="h-5 w-5" style={{ color: f.c }} />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{f.t}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* How */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 border-t border-border">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mb-14">{t("landing.how_title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="relative rounded-xl bg-[#151A22] border border-border p-7">
              <div className="font-heading font-bold text-5xl text-primary/30 mb-3">0{i + 1}</div>
              <h3 className="font-heading font-bold text-lg mb-2">{s.t}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <Button data-testid="bottom-cta-btn" onClick={user ? () => (window.location.href = "/dashboard") : login}
            className="bg-primary text-white hover:bg-[#6D28D9] h-12 px-7 rounded-lg text-base shadow-[0_0_20px_rgba(124,58,237,0.35)]">
            {t("landing.cta_primary")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {t("brand")}</span>
          <span className="font-mono-fin">{t("tagline")}</span>
        </div>
      </footer>
    </div>
  );
}
